import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MdArrowBack } from "react-icons/md";
import * as apicall from "../../components/Apicall";
import { getBookingLabel } from "../../utils/industryLabels";
import ConfirmationModal from "../../components/Appointment/ConfirmationModal";
import AssetFieldsForm from "../../components/CustomerAssets/AssetFieldsForm";

const STATUS_COLORS = {
  Active: { bg: "#dcfce7", text: "#16a34a" },
  Inactive: { bg: "#fee2e2", text: "#dc2626" },
};

const BOOKING_STATUS_COLORS = {
  Completed: { bg: "#dcfce7", text: "#16a34a" },
  "In Progress": { bg: "#ede9fe", text: "#7c3aed" },
  Upcoming: { bg: "#dbeafe", text: "#1d4ed8" },
  Cancelled: { bg: "#fee2e2", text: "#dc2626" },
};

function initials(first, last) {
  const a = (first || "").trim()[0] || "";
  const b = (last || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}

function fullName(c) {
  return [c?.first_name, c?.last_name].filter(Boolean).join(" ") || "—";
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" });
}

function formatTime(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  if (h === undefined) return timeStr;
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${m} ${period}`;
}

const emptyAddressForm = { location: "", house_no: "", area: "", landmark: "", state: "", label: "" };
const emptyAssetForm = { label: "", field_values: {}, custom_fields: [] };

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // A dependent (family member booked for under a customer's own number)
  // is a fully separate patient record living in a different backend table
  // (ezp_user_dependents, not ezp_customers) — its numeric id can collide
  // with an unrelated customer's id, so the route itself is what tells us
  // which table/endpoint to use, not the id alone.
  const recordType = location.pathname.includes("/customers/profile/dependent/") ? "dependent" : "customer";
  const detailEndpoint = recordType === "dependent" ? `ezp_dependents/${id}` : `ezp_customers/${id}`;

  // const userDetails = (() => {
  //   try {
  //     const d = localStorage.getItem("userDetails");
  //     return d && d !== "undefined" ? JSON.parse(d) : null;
  //   } catch {
  //     return null;
  //   }
  // })();

  // Paint instantly from the row the user clicked, then refresh with the real detail payload
  const [customer, setCustomer] = useState(location.state?.customerData || null);
    const [industry, setIndustry] = useState(location.state?.industry || null);
    const [addresses, setAddresses] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  // Recent bookings/appointments table is paginated client-side — the
  // detail endpoint already returns the customer's full booking history in
  // one response, so there's no server-side page param to drive off of.
  const BOOKINGS_PAGE_SIZE = 5;
  const [bookingsPage, setBookingsPage] = useState(1);

    const currency = industry?.currency || "AED";
  const fmtMoney = (n) => `${currency} ${Number(n || 0).toLocaleString("en", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  const isClinicService = industry?.industry === 1;
  const isSportsService = industry?.industry === 7;
  const CUSTOMER_TERM = isClinicService ? "Patient" : isSportsService ? "Player" : "Customer";
  const CUSTOMER_TERM_PLURAL = isClinicService ? "Patients" : isSportsService ? "Players" : "Customers";
  const BOOKING_TERM = getBookingLabel(industry);
  const BOOKING_TERM_PLURAL = getBookingLabel(industry, true);


  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apicall.get(detailEndpoint);
      if (res?.success) {
        setCustomer(res.data.customer);
        setAddresses(res.data.addresses || []);
        setBookings(res.data.bookings || []);
        setBookingsPage(1);
      }
    } catch (err) {
      console.error("fetchDetail error:", err);
    } finally {
      setLoading(false);
    }
  }, [detailEndpoint]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // ── Edit customer basic info ───────────────────────────────
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  // Snapshot of what openEdit loaded, so saveEdit can tell "untouched" apart
  // from "actively cleared" — a record whose last name/email was already
  // blank in the backend shouldn't error just because the modal was opened
  // and saved without any edits.
  const [originalEditForm, setOriginalEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");
  // Per-field messages so validation shows right under the offending input
  // instead of one shared banner at the bottom.
  const [editFieldErrors, setEditFieldErrors] = useState({});

  const openEdit = () => {
    const initial = {
      first_name: customer?.first_name || "",
      last_name: customer?.last_name || "",
      phone_number: customer?.phone_number || "",
      email: customer?.email || "",
    };
    setEditForm(initial);
    setOriginalEditForm(initial);
    setEditError("");
    setEditFieldErrors({});
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      setSavingEdit(true);
      setEditError("");
      setEditFieldErrors({});

      const hasChanges = originalEditForm && Object.keys(editForm).some(
        (key) => editForm[key] !== originalEditForm[key]
      );
      if (!hasChanges) {
        setEditError("No changes detected");
        setSavingEdit(false);
        return;
      }

      // Phone Number is read-only in this modal (see the disabled input
      // below) — it's the record's identity field.
      //
      // Validation only applies to fields the user actually edited. A field
      // left untouched goes through as-is even if it was already blank in
      // the backend — it's the user's call whether to fill it in, not
      // something we should force on an edit they didn't ask for. A field
      // the user *did* touch and cleared out, though, is a real "you just
      // deleted required data" case and gets blocked.
      const touched = (key) => editForm[key] !== originalEditForm[key];
      const fieldErrors = {};

      if (touched("first_name") && !editForm.first_name?.trim()) {
        fieldErrors.first_name = "First name is required";
      }
      if (touched("last_name") && !editForm.last_name?.trim()) {
        fieldErrors.last_name = "Last name is required";
      }
      // Dependents have no email field in this form at all, so only
      // enforce it for customer records.
      if (recordType !== "dependent" && touched("email")) {
        const emailVal = editForm.email?.trim() || "";
        if (!emailVal) {
          fieldErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
          fieldErrors.email = "Enter a valid email address";
        }
      }

      if (Object.keys(fieldErrors).length > 0) {
        setEditFieldErrors(fieldErrors);
        setSavingEdit(false);
        return;
      }

      const payload = {
        ...editForm,
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        ...(recordType !== "dependent" ? { email: editForm.email.trim() } : {}),
      };

      const res = await apicall.post(payload, detailEndpoint);
      if (res?.success) {
        setEditing(false);
        await fetchDetail();
      }
    } catch (err) {
      console.error("saveEdit error:", err);
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Address add/edit/delete ──────────────────────────────────
  const [addressModal, setAddressModal] = useState(null); // { mode, id?, form }
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressError, setAddressError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Same physical address (house/area/street/landmark/state), ignoring the
  // label — "Home" and "Office" pointing at identical fields is still a dup.
  const ADDRESS_DUP_FIELDS = ["house_no", "area", "location", "landmark", "state"];
  const isSameAddress = (a, b) =>
    ADDRESS_DUP_FIELDS.every(
      (f) => (a[f] || "").trim().toLowerCase() === (b[f] || "").trim().toLowerCase()
    );

  const openAddAddress = () => {
    setAddressError("");
    setAddressModal({ mode: "add", form: { ...emptyAddressForm } });
  };
  const openEditAddress = (addr) => {
    setAddressError("");
    setAddressModal({
      mode: "edit",
      id: addr.id,
      form: {
        location: addr.location || "",
        house_no: addr.house_no || "",
        area: addr.area || "",
        landmark: addr.landmark || "",
        state: addr.state || "",
        label: addr.label || "",
      },
    });
  };

  const saveAddress = async () => {
    try {
      setSavingAddress(true);
      setAddressError("");

      const isDuplicate = addresses.some(
        (a) =>
          (addressModal.mode !== "edit" || a.id !== addressModal.id) &&
          isSameAddress(a, addressModal.form)
      );
      if (isDuplicate) {
        setAddressError("This address is already saved for this " + CUSTOMER_TERM.toLowerCase() + ".");
        setSavingAddress(false);
        return;
      }

      if (addressModal.mode === "add") {
        await apicall.post({ user_id: id, ...addressModal.form }, "ezp_customer_address");
      } else {
        await apicall.post(addressModal.form, `ezp_customer_address/${addressModal.id}`);
      }
      setAddressModal(null);
      await fetchDetail();
    } catch (err) {
      console.error("saveAddress error:", err);
    } finally {
      setSavingAddress(false);
    }
  };

  const confirmDeleteAddress = async () => {
    try {
      await apicall.get(`delete_ezp_customer_address/${deleteTarget}`);
      setDeleteTarget(null);
      await fetchDetail();
    } catch (err) {
      console.error("deleteAddress error:", err);
    }
  };

  // ── Customer Assets (e.g. Vehicles for a car-service vendor, Preferences
  //    for a salon) - platform-wide per-industry schema, seeded via SQL, not
  //    admin-manageable from this screen yet. Hidden entirely when the
  //    vendor's industry has no seeded asset type (schema.asset_type null),
  //    and for dependents (no ezp_customers row to attach assets to). ──
  const [assetSchema, setAssetSchema] = useState({ asset_type: null, fields: [] });
  const [assets, setAssets] = useState([]);
  const [assetModal, setAssetModal] = useState(null); // { mode, id?, form }
  const [savingAsset, setSavingAsset] = useState(false);
  const [deleteAssetTarget, setDeleteAssetTarget] = useState(null);

  const fetchAssets = useCallback(async () => {
    if (recordType !== "customer" || !id) return;
    try {
      const res = await apicall.get(`ezp_customer_assets/${id}`);
      if (res?.success) setAssets(res.data || []);
    } catch (err) {
      console.error("fetchAssets error:", err);
    }
  }, [recordType, id]);

  useEffect(() => {
    if (recordType !== "customer") return;
    (async () => {
      try {
        const res = await apicall.get("ezp_customer_asset_schema");
        if (res?.success) setAssetSchema(res.data);
      } catch (err) {
        console.error("fetchAssetSchema error:", err);
      }
    })();
    fetchAssets();
  }, [recordType, fetchAssets]);

  const openAddAsset = () => setAssetModal({ mode: "add", form: { ...emptyAssetForm } });
  const openEditAsset = (asset) => setAssetModal({
    mode: "edit",
    id: asset.id,
    form: {
      label: asset.label || "",
      field_values: asset.field_values || {},
      custom_fields: Object.entries(asset.custom_fields || {}).map(([key, value]) => ({ key, value })),
    },
  });

  const saveAsset = async () => {
    try {
      setSavingAsset(true);
      const customFieldsObj = {};
      (assetModal.form.custom_fields || []).forEach((cf) => { if (cf.key) customFieldsObj[cf.key] = cf.value; });
      const payload = {
        label: assetModal.form.label,
        field_values: assetModal.form.field_values,
        custom_fields: customFieldsObj,
      };
      if (assetModal.mode === "add") {
        await apicall.post({ customer_id: id, asset_type_id: assetSchema.asset_type.id, ...payload }, "ezp_customer_assets");
      } else {
        await apicall.post(payload, `ezp_customer_assets/${assetModal.id}`);
      }
      setAssetModal(null);
      await fetchAssets();
    } catch (err) {
      console.error("saveAsset error:", err);
    } finally {
      setSavingAsset(false);
    }
  };

  const confirmDeleteAsset = async () => {
    try {
      await apicall.post({}, `delete_customer_asset/${deleteAssetTarget}`);
      setDeleteAssetTarget(null);
      await fetchAssets();
    } catch (err) {
      console.error("deleteAsset error:", err);
    }
  };

  // Short preview line for an asset row - first couple of predefined field values, comma-joined.
  const assetPreview = (asset) => {
    const vals = assetSchema.fields.map((f) => asset.field_values?.[f.field_key]).filter(Boolean);
    return vals.slice(0, 3).join(", ") || "—";
  };

  if (loading && !customer) {
    return (
      <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "24px 28px" }}>
        <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 5px rgba(0,0,0,0.06)", padding: 56, textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
          Loading {CUSTOMER_TERM.toLowerCase()}…
        </div>
      </div>
    );
  }

  if (!loading && !customer) {
    return (
      <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "24px 28px" }}>
        <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 5px rgba(0,0,0,0.06)", padding: 56, textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
          {CUSTOMER_TERM} not found.
          <div style={{ marginTop: 14 }}>
            <button onClick={() => navigate("/customers")} style={{ padding: "8px 18px", border: "none", borderRadius: 9, background: "#d01c4c", color: "#d01c4c", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              ← Back to {CUSTOMER_TERM_PLURAL}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "24px 28px" }}>
      <button
        onClick={() => navigate("/customers")}
        style={{ display: "flex", alignItems: "center", gap: 6, border: "none", background: "none", color: "#d01c4c", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 18 }}
      >
        <MdArrowBack size={16} /> Back to {CUSTOMER_TERM_PLURAL}
      </button>

      {/* Profile header */}
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 5px rgba(0,0,0,0.06)", padding: 22, marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 14 }}>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#e0e7ff", color: "#4338ca", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700 }}>
            {initials(customer?.first_name, customer?.last_name)}
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 19, fontWeight: 800, color: "#0f172a" }}>{fullName(customer)}</span>
              {/* <span
                style={{
                  padding: "3px 10px",
                  borderRadius: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  background: STATUS_COLORS[customer?.is_active ? "Active" : "Inactive"].bg,
                  color: STATUS_COLORS[customer?.is_active ? "Active" : "Inactive"].text,
                }}
              >
                {customer?.is_active ? "Active" : "Inactive"}
              </span> */}
            </div>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              {customer?.phone_number || "—"} &nbsp;&nbsp; {customer?.email || ""}
            </div>
            {recordType === "dependent" && customer?.parent_name && (
              <div style={{ fontSize: 12.5, color: "#4f46e5", fontWeight: 600, marginTop: 2 }}>
                Family member of {customer.parent_name}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={openEdit}
          style={{ padding: "8px 16px", border: "1.5px solid #e2e8f0", borderRadius: 9, background: "#fff", fontSize: 12.5, fontWeight: 700, color: "#334155", cursor: "pointer" }}
        >
          ✎ Edit Information
        </button>
      </div>

      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 18 }}>
        {/* Basic info card */}
        <div style={{ flex: "1 1 260px", background: "#fff", borderRadius: 16, boxShadow: "0 1px 5px rgba(0,0,0,0.06)", padding: 20 }}>
          <h4 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Basic Information</h4>
          {[
            [`${CUSTOMER_TERM} Since`, formatDate(customer?.created)],
            // ["Status", customer?.is_active ? "Active" : "Inactive"],
            [`Total ${BOOKING_TERM_PLURAL}`, customer?.total_bookings ?? 0],
            ["Total Spent", fmtMoney(customer?.total_spent)],
          ].map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f8fafc", fontSize: 13 }}>
              <span style={{ color: "#94a3b8" }}>{label}</span>
              <span style={{ color: "#0f172a", fontWeight: 600 }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Addresses card — hidden for clinic industry entirely, and for
            dependents under any industry (no address storage exists for
            them in the schema). */}
        {!isClinicService && recordType === "customer" && (
               <div style={{ flex: "1 1 320px", background: "#fff", borderRadius: 16, boxShadow: "0 1px 5px rgba(0,0,0,0.06)", padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Addresses ({addresses.length})</h4>
            <button onClick={openAddAddress} style={{ border: "none", background: "none", color: "#4f46e5", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
              + Add Address
            </button>
          </div>
          {addresses.length === 0 ? (
            <p style={{ fontSize: 13, color: "#94a3b8" }}>No addresses saved yet.</p>
          ) : (
            addresses.map((addr, idx) => (
              <div key={addr.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: idx < addresses.length - 1 ? "1px solid #f8fafc" : "none" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{addr.label || "Address"}</span>
                    {idx === 0 && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#4f46e5", background: "#eef2ff", padding: "2px 7px", borderRadius: 20 }}>Default</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 2 }}>
                    {[addr.house_no, addr.area, addr.location, addr.state].filter(Boolean).join(", ") || "—"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button onClick={() => openEditAddress(addr)} style={{ border: "none", background: "none", cursor: "pointer", color: "#64748b", fontSize: 13 }} title="Edit">✎</button>
                  <button onClick={() => setDeleteTarget(addr.id)} style={{ border: "none", background: "none", cursor: "pointer", color: "#dc2626", fontSize: 13 }} title="Delete">🗑</button>
                </div>
              </div>
            ))
          )}
        </div>
        )
        }

        {/* Customer Assets card - platform-wide per-industry configurable
            (e.g. Vehicles for a car-service vendor). Hidden when the
            vendor's industry has no seeded asset type, and for dependents. */}
        {recordType === "customer" && assetSchema.asset_type && (
          <div style={{ flex: "1 1 320px", background: "#fff", borderRadius: 16, boxShadow: "0 1px 5px rgba(0,0,0,0.06)", padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{assetSchema.asset_type.label}s ({assets.length})</h4>
              <button onClick={openAddAsset} style={{ border: "none", background: "none", color: "#4f46e5", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                + Add {assetSchema.asset_type.label}
              </button>
            </div>
            {assets.length === 0 ? (
              <p style={{ fontSize: 13, color: "#94a3b8" }}>No {assetSchema.asset_type.label.toLowerCase()}s saved yet.</p>
            ) : (
              assets.map((asset, idx) => (
                <div key={asset.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: idx < assets.length - 1 ? "1px solid #f8fafc" : "none" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{asset.label || `${assetSchema.asset_type.label} ${idx + 1}`}</div>
                    <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 2 }}>{assetPreview(asset)}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    <button onClick={() => openEditAsset(asset)} style={{ border: "none", background: "none", cursor: "pointer", color: "#64748b", fontSize: 13 }} title="Edit">✎</button>
                    <button onClick={() => setDeleteAssetTarget(asset.id)} style={{ border: "none", background: "none", cursor: "pointer", color: "#dc2626", fontSize: 13 }} title="Delete">🗑</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

      </div>

      {/* Recent bookings */}
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 5px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9" }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Recent {BOOKING_TERM_PLURAL}</span>
        </div>
        {bookings.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>No {BOOKING_TERM_PLURAL.toLowerCase()} yet</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.4fr 1.4fr 1.4fr 1fr 1fr", padding: "9px 20px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
              {[`${BOOKING_TERM.toUpperCase()} ID`,isClinicService ? "Specialization" : "SERVICE", isClinicService ? "DOCTOR" : "CREW", "DATE & TIME", "STATUS", "AMOUNT"].map((h) => (
                <div key={h} style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.4 }}>{h}</div>
              ))}
            </div>
            {bookings
              .slice((bookingsPage - 1) * BOOKINGS_PAGE_SIZE, bookingsPage * BOOKINGS_PAGE_SIZE)
              .map((b, i, page) => {
                const statusName = b.status_name || "—";
                const sc = BOOKING_STATUS_COLORS[statusName] || { bg: "#f1f5f9", text: "#64748b" };
                return (
                  <div key={b.id} style={{ display: "grid", gridTemplateColumns: "1.2fr 1.4fr 1.4fr 1.4fr 1fr 1fr", padding: "12px 20px", alignItems: "center", borderBottom: i < page.length - 1 ? "1px solid #f8fafc" : "none" }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1e293b" }}>{b.display_booking_id || b.id}</div>
                    <div>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a" }}>{b.service_name || "—"}</div>
                      {b.service_type_name && <div style={{ fontSize: 11, color: "#94a3b8" }}>{b.service_type_name}</div>}
                    </div>
                    <div style={{ fontSize: 12.5, color: "#475569" }}>{b.crew_names || "—"}</div>
                    <div style={{ fontSize: 12.5, color: "#475569" }}>
                      {formatDate(b.service_date)}
                      {b.service_time && <div style={{ fontSize: 11, color: "#94a3b8" }}>{formatTime(b.service_time)}</div>}
                    </div>
                    <div>
                      <span style={{ padding: "3px 9px", borderRadius: 20, background: sc.bg, color: sc.text, fontSize: 10.5, fontWeight: 700 }}>{statusName}</span>
                    </div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>{fmtMoney(b.total)}</div>
                  </div>
                );
              })}

            {/* Same pagination footer as Customers.jsx (page range · total,
                ‹ prev, up to 5 numbered pages, next ›) for consistency. */}
            {(() => {
              const totalPages = Math.max(1, Math.ceil(bookings.length / BOOKINGS_PAGE_SIZE));
              return (
                <div style={{ padding: "16px 20px", borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                  <span style={{ fontSize: 13, color: "#64748b" }}>
                    Page {bookingsPage} of {totalPages} · {bookings.length} total {BOOKING_TERM_PLURAL.toLowerCase()}
                  </span>
                  {totalPages > 1 && (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <button
                        onClick={() => setBookingsPage((p) => Math.max(1, p - 1))}
                        disabled={bookingsPage <= 1}
                        style={{ padding: "6px 12px", border: "1.5px solid #e2e8f0", borderRadius: 7, fontSize: 13, fontWeight: 600, background: bookingsPage <= 1 ? "#f8fafc" : "#fff", color: bookingsPage <= 1 ? "#cbd5e1" : "#334155", cursor: bookingsPage <= 1 ? "default" : "pointer" }}
                      >
                        ‹
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, idx) => {
                        const start = Math.max(1, Math.min(bookingsPage - 2, totalPages - 4));
                        const p = start + idx;
                        if (p > totalPages) return null;
                        return (
                          <button
                            key={p}
                            onClick={() => setBookingsPage(p)}
                            style={{ padding: "6px 12px", border: `1.5px solid ${p === bookingsPage ? "#4f46e5" : "#e2e8f0"}`, borderRadius: 7, fontSize: 13, fontWeight: p === bookingsPage ? 700 : 500, background: p === bookingsPage ? "#eef2ff" : "#fff", color: p === bookingsPage ? "#4f46e5" : "#334155", cursor: "pointer", minWidth: 32 }}
                          >
                            {p}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setBookingsPage((p) => Math.min(totalPages, p + 1))}
                        disabled={bookingsPage >= totalPages}
                        style={{ padding: "6px 12px", border: "1.5px solid #e2e8f0", borderRadius: 7, fontSize: 13, fontWeight: 600, background: bookingsPage >= totalPages ? "#f8fafc" : "#fff", color: bookingsPage >= totalPages ? "#cbd5e1" : "#334155", cursor: bookingsPage >= totalPages ? "default" : "pointer" }}
                      >
                        ›
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* Edit customer modal */}
      {editing && editForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 24, width: "100%", maxWidth: 420 }}>
            <h4 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Edit {CUSTOMER_TERM} Information</h4>
            {[
              ["First Name", "first_name"],
              ["Last Name", "last_name"],
              ["Phone Number", "phone_number"],
              // Dependents have no email column to save this to.
              ...(recordType === "dependent" ? [] : [["Email", "email"]]),
            ].map(([label, key]) => {
              // Phone Number is the record's identity field — locked instead
              // of validated, so it can't be edited here. Every other field
              // (including First Name) is editable.
              const isLocked = key === "phone_number";
              return (
                <div key={key} style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5 }}>
                    {label}
                  </label>
                  <input
                    type="text"
                    value={editForm[key]}
                    disabled={isLocked}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEditForm((f) => ({ ...f, [key]: val }));
                      if (editFieldErrors[key]) setEditFieldErrors((fe) => ({ ...fe, [key]: "" }));
                    }}
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: 8,
                      border: `1.5px solid ${editFieldErrors[key] ? "#dc2626" : "#e2e8f0"}`,
                      fontSize: 13,
                      boxSizing: "border-box",
                      outline: "none",
                      background: isLocked ? "#f1f5f9" : "#fff",
                      color: isLocked ? "#94a3b8" : "#0f172a",
                      cursor: isLocked ? "not-allowed" : "text",
                    }}
                  />
                  {editFieldErrors[key] && (
                    <div style={{ color: "#dc2626", fontSize: 11.5, marginTop: 4 }}>{editFieldErrors[key]}</div>
                  )}
                </div>
              );
            })}
            {editError && (
              <div style={{ color: "#dc2626", fontSize: 12.5, marginBottom: 12 }}>{editError}</div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button onClick={saveEdit} disabled={savingEdit} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "none", background: "#d01c4c", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                {savingEdit ? "Saving…" : "Save Changes"}
              </button>
              <button onClick={() => setEditing(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "1.5px solid #e2e8f0", background: "#fff", color: "#334155", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit address modal */}
      {addressModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 24, width: "100%", maxWidth: 460 }}>
            <h4 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
              {addressModal.mode === "add" ? "Add Address" : "Edit Address"}
            </h4>
            {[
              ["Label (e.g. Home, Office)", "label"],
              ["Address / Street", "location"],
              ["House / Flat No.", "house_no"],
              ["Area", "area"],
              ["Landmark", "landmark"],
              ["State / City", "state"],
            ].map(([label, key]) => (
              <div key={key} style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5 }}>{label}</label>
                <input
                  type="text"
                  value={addressModal.form[key]}
                  onChange={(e) => setAddressModal((m) => ({ ...m, form: { ...m.form, [key]: e.target.value } }))}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13, boxSizing: "border-box", outline: "none" }}
                />
              </div>
            ))}
            {addressError && (
              <div style={{ color: "#dc2626", fontSize: 12.5, marginBottom: 12 }}>{addressError}</div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button onClick={saveAddress} disabled={savingAddress} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "none", background: "#dc2626", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                {savingAddress ? "Saving…" : "Save Address"}
              </button>
              <button onClick={() => setAddressModal(null)} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "1.5px solid #e2e8f0", background: "#fff", color: "#334155", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        show={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Address"
        message="Are you sure you want to delete this address? This cannot be undone."
        primaryText="Delete"
        secondaryText="Cancel"
        onPrimary={confirmDeleteAddress}
        onSecondary={() => setDeleteTarget(null)}
      />

      {/* Add/Edit asset modal */}
      {assetModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 24, width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto" }}>
            <h4 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
              {assetModal.mode === "add" ? `Add ${assetSchema.asset_type?.label}` : `Edit ${assetSchema.asset_type?.label}`}
            </h4>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5 }}>
                Label (e.g. "My Toyota")
              </label>
              <input
                type="text"
                value={assetModal.form.label}
                onChange={(e) => setAssetModal((m) => ({ ...m, form: { ...m.form, label: e.target.value } }))}
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13, boxSizing: "border-box", outline: "none" }}
              />
            </div>
            <AssetFieldsForm
              fieldDefs={assetSchema.fields}
              values={assetModal.form.field_values}
              onChange={(key, val) => setAssetModal((m) => ({ ...m, form: { ...m.form, field_values: { ...m.form.field_values, [key]: val } } }))}
              customFields={assetModal.form.custom_fields}
              onCustomFieldsChange={(next) => setAssetModal((m) => ({ ...m, form: { ...m.form, custom_fields: next } }))}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button onClick={saveAsset} disabled={savingAsset} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "none", background: "#d01c4c", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                {savingAsset ? "Saving…" : `Save ${assetSchema.asset_type?.label}`}
              </button>
              <button onClick={() => setAssetModal(null)} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: "1.5px solid #e2e8f0", background: "#fff", color: "#334155", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        show={!!deleteAssetTarget}
        onClose={() => setDeleteAssetTarget(null)}
        title={`Delete ${assetSchema.asset_type?.label}`}
        message={`Are you sure you want to delete this ${assetSchema.asset_type?.label?.toLowerCase()}? This cannot be undone.`}
        primaryText="Delete"
        secondaryText="Cancel"
        onPrimary={confirmDeleteAsset}
        onSecondary={() => setDeleteAssetTarget(null)}
      />
    </div>
  );
}
